import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { ExportService } from '../../../shared/services/export.service';
import { Ledger, LedgerEntry } from '../ledger';
import { BankService } from '../../../shared/services/bank.service';
import { Subscription } from 'rxjs/Subscription';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { TdDialogService, TdLoadingService, TdDataTableSortingOrder, TdSearchBoxComponent, ITdDataTableSortChangeEvent, IPageChangeEvent, ITdDataTableColumn, TdDataTableService } from '@covalent/core';
import { AuthenticationService } from '../../../shared/services/authentication.service';
import { SettingsService } from '../../../shared/services/settings.service';
import { ReportService } from '../../../shared/services/report.service';
import { DateRangeDialogComponent } from '../../../shared/dialogs/date-range-dialog/date-range-dialog.component';
import {Utils} from '../../../shared/utils';

@Component({
  selector: 'app-account-ledger',
  templateUrl: './account-ledger.component.html',
  styleUrls: ['./account-ledger.component.scss'],
  providers: [BankService, ReportService]
})
export class AccountLedgerComponent implements OnInit, OnDestroy {
  @ViewChild('searchBox') _searchBox: TdSearchBoxComponent;

  subscription: Subscription = new Subscription();
  currentLedger: Ledger = new Ledger();

  columns: ITdDataTableColumn[] = [
    { name: 'transaction', label: 'Transaction' },
    { name: 'last_updated', label: 'Date' },
    { name: 'description', label: 'Description' },
    { name: 'in', label: 'In' },
    { name: 'out', label: 'Out' },
    { name: 'vat', label: 'VAT' },
    { name: 'balance', label: 'Balance' },
  ];

  filteredData: any[];
  filteredTotal: number;
  fromRow: number = 1;
  currentPage: number = 1;
  sortBy: string = 'transaction';
  selectedRows: any[] = [];
  sortOrder: TdDataTableSortingOrder = TdDataTableSortingOrder.Descending;
  selectable: boolean = true;
  multipleSelect: boolean = true;

  constructor(
    private _bankService: BankService,
    public _exportService: ExportService,
    private _settingsService: SettingsService,
    private _reportService: ReportService,
    public _authService: AuthenticationService,
    private _dialog: TdDialogService,
    private _loadingService: TdLoadingService,
    private _dataTableService: TdDataTableService,
    private route: ActivatedRoute
  ) { }

  ngOnInit() {
    this._loadingService.register('loading');

    this.presetSearchBox();

    this.subscription.add(
      this.route.paramMap.subscribe(
        (params: ParamMap) => {
          this.subscription.add(
            this._bankService.getLedger(params.get('id')).subscribe(
              response => {
                this.currentLedger = response;
              },
              error => {
                this._dialog.openAlert({ title: 'Error', message: 'Could not retrieve data' });
                this._loadingService.resolve('loading');
              },
              () => {
                this._loadingService.resolve('loading');
                this.filter()
              }
            )
          );
        }
      )
    );
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  cashReport() {
    this._dialog.open(DateRangeDialogComponent).afterClosed().subscribe(
      result => {
        if (result) {
          let tempLedger: Ledger = Object.assign({}, this.currentLedger);
          tempLedger.entries = Utils.sortData(tempLedger.entries, this.sortBy, this.sortOrder);

          console.log('total items: ' + tempLedger.entries.length);
          
          let preItems: LedgerEntry[] = tempLedger.entries.filter((e: LedgerEntry) => { return Utils.dateLessThan(result.from, e.last_updated) });

          console.log('pre items: ' + preItems.length);
          console.log('pre item (initial) balance:' + preItems[0].balance);
          //console.log(preItems);
          
          let includedItems: LedgerEntry[] = tempLedger.entries.filter((e: LedgerEntry) => { return Utils.datesWithinRange(result.from, result.to, e.last_updated) });

          console.log('included items: ' + includedItems.length);
          //console.log(includedItems);

          if (preItems.length > 0) {
            // There are items before the FROM date, create an entry to summarize as an initial balance
            let startingBalance: LedgerEntry = {
              id: 0,
              transaction: 0,
              date: result.from,
              last_updated: result.from,
              description: 'Solduri Initiale ' + result.from,
              in: preItems[0].balance, //Last balance of preItems OR preItems.reduce((a, b) => { return a + b.in - b.out - b.vat }, 0)
              out: 0,
              vat: 0,
              balance: preItems[0].balance
            }
            tempLedger.entries = includedItems.concat([startingBalance]);
          } else {
            // We got a date BEFORE the initial deposit
            tempLedger.entries = includedItems;
          }

          if (tempLedger.entries.length > 0) {
            this._reportService.cashReport(tempLedger, result.from, result.to);
          } else {
            this._dialog.openAlert({ title: 'Error', message: 'No Entries Found In Selected Dates' });
          }
          
        }


      }
    );
  }

  presetSearchBox() {
    if (this._settingsService.getSearchTerm('ledger')) {
      this._searchBox.value = this._settingsService.getSearchTerm('ledger');
      this._searchBox.searchClicked();
    }
  }

  sort(sortEvent: ITdDataTableSortChangeEvent): void {
    //console.log("Sort by: " + sortEvent.name + " , "+ sortEvent.order);
    this.sortBy = sortEvent.name;
    
    this.sortOrder = sortEvent.order === TdDataTableSortingOrder.Ascending
    ? TdDataTableSortingOrder.Descending
    : TdDataTableSortingOrder.Ascending;

    this.filter();
  }

  search(searchTerm: string): void {
    this._settingsService.setSearchTerm('ledger', searchTerm);
    this.filter();
  }

  page(pagingEvent: IPageChangeEvent): void {
    this.fromRow = pagingEvent.fromRow;
    this.currentPage = pagingEvent.page;
    this._settingsService.setPageSize(pagingEvent.pageSize);
    this.filter();
  }

  filter(): void {
    let newData: any[] = this.currentLedger.entries;
    let excludedColumns: string[] = this.columns
      .filter((column: ITdDataTableColumn) => {
        return ((column.filter === undefined && column.hidden === true) ||
          (column.filter !== undefined && column.filter === false));
      }).map((column: ITdDataTableColumn) => {
        return column.name;
      });
    newData = this._dataTableService.filterData(newData, this._settingsService.getSearchTerm('ledger'), true, excludedColumns);
    this.filteredTotal = newData.length;
    console.log("Sort by: " + this.sortBy + " , " + this.sortOrder);
    newData = Utils.sortData(newData, this.sortBy, this.sortOrder);
    if (this._settingsService.getPageSize() + '' != 'All') {
      newData = this._dataTableService.pageData(newData, this.fromRow, this.currentPage * this._settingsService.getPageSize());
    }
    this.filteredData = newData;
  }

}



// WEBPACK FOOTER //
// /home/mysticerp/domains/mysticerp.ro/public_html/mystic/agatexfe/src/app/agatextscreens/accounting/account-ledger/account-ledger.component.ts